/**
 * Lazy patient summary for Spotlight: next appointment + outstanding treatment debt.
 * Kept outside GlobalSearchHit / search transport so fan-out stays thin.
 */

import { appointmentAPI } from '@/modules/appointment/api/appointment.api';
import { treatmentPlanAPI } from '@/modules/treatment-plan/api/treatmentPlan.api';

const NEXT_APPOINTMENT_STATUSES = new Set(['scheduled', 'confirmed', 'arrived']);

export type PatientSpotlightSummary = {
  nextAppointmentAt: string | null;
  outstandingBalance: number | null;
};

const isAbortError = (err: unknown): boolean => {
  const e = err as { name?: string; code?: string };
  return (
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError' ||
    e?.code === 'ERR_CANCELED'
  );
};

/** Exported for unit tests — mirrors list enrichment on GET /patients. */
export const pickNextAppointmentAt = (
  appointments: { appointmentDate: string; status: string }[],
  nowMs: number,
): string | null => {
  const upcoming = appointments
    .filter(
      (appt) =>
        NEXT_APPOINTMENT_STATUSES.has(appt.status) &&
        new Date(appt.appointmentDate).getTime() >= nowMs,
    )
    .sort(
      (a, b) =>
        new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime(),
    );
  return upcoming[0]?.appointmentDate ?? null;
};

/** Exported for unit tests — same rule as patient Payments tab. */
export const sumOutstandingBalance = (
  plans: { balanceAmount?: number }[],
): number =>
  plans.reduce((sum, plan) => sum + (plan.balanceAmount ?? 0), 0);

export type FetchPatientSpotlightSummaryOptions = {
  includeNextAppointment: boolean;
  includeOutstandingBalance: boolean;
  signal?: AbortSignal;
};

/**
 * Parallel fetch of the fields Spotlight needs. Skipped sections stay null.
 */
export const fetchPatientSpotlightSummary = async (
  patientId: string,
  options: FetchPatientSpotlightSummaryOptions,
): Promise<PatientSpotlightSummary> => {
  const { includeNextAppointment, includeOutstandingBalance, signal } = options;

  const result: PatientSpotlightSummary = {
    nextAppointmentAt: null,
    outstandingBalance: null,
  };

  if (!includeNextAppointment && !includeOutstandingBalance) {
    return result;
  }

  const tasks: Promise<void>[] = [];

  if (includeNextAppointment) {
    tasks.push(
      (async () => {
        try {
          const res = await appointmentAPI.getPatientAppointments(patientId);
          if (signal?.aborted) return;
          result.nextAppointmentAt = pickNextAppointmentAt(res.data ?? [], Date.now());
        } catch (err) {
          if (signal?.aborted || isAbortError(err)) return;
          throw err;
        }
      })(),
    );
  }

  if (includeOutstandingBalance) {
    tasks.push(
      (async () => {
        try {
          const res = await treatmentPlanAPI.getAll({ patientId });
          if (signal?.aborted) return;
          result.outstandingBalance = sumOutstandingBalance(res.data ?? []);
        } catch (err) {
          if (signal?.aborted || isAbortError(err)) return;
          throw err;
        }
      })(),
    );
  }

  const settled = await Promise.allSettled(tasks);
  if (signal?.aborted) {
    return result;
  }

  const rejected = settled.find((row) => row.status === 'rejected');
  if (rejected && rejected.status === 'rejected') {
    throw rejected.reason;
  }

  return result;
};
